import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { ANGOLA_PROVINCES, INTERESTS, LOOKING_FOR } from "@/lib/i18n";
import { LogOut, Shield, ShieldCheck, Settings, Download, Trash2, AlertTriangle, Bell, Heart, Sparkles, MapPinned } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import LegalFooter from "@/components/LegalFooter";
import { TutorialsSection } from "@/components/Onboarding";
import { useResendVerification } from "@/components/VerifyEmailSheet";
import { ProfilePhotoEditor } from "@/components/profile/ProfilePhotoEditor";
import { ProfilePosts } from "@/components/profile/ProfilePosts";
import { BlockedUsersSection } from "@/components/profile/BlockedUsersSection";
import { MailCheck, MailWarning } from "lucide-react";

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: user.name || "",
    city: user.city || "",
    province: user.province || "",
    bio: user.bio || "",
    interests: user.interests || [],
    looking_for: user.looking_for || [],
    profile_visible: user.profile_visible !== false,
  });
  const [busy, setBusy] = useState(false);
  const [destinationsOn, setDestinationsOn] = useState(false);
  useEffect(() => { api.get("/communities/config").then((r) => setDestinationsOn(!!r.data.post_destinations_enabled)).catch(() => {}); }, []);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");

  const toggle = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/profile", form);
      setUser(data);
      toast.success("Perfil atualizado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/profile/export/me");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `angolauk-meus-dados-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dados exportados");
    } catch (e) {
      toast.error("Erro ao exportar");
    }
  };

  const deleteAccount = async () => {
    if (deleteText.trim().toUpperCase() !== "ELIMINAR") {
      return toast.error('Escreve "ELIMINAR" para confirmar');
    }
    setDeleting(true);
    try {
      await api.delete("/profile/me");
      toast.success("Conta eliminada");
      await logout();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao eliminar");
    } finally { setDeleting(false); }
  };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold">{t("profile")}</h1>
        <button onClick={logout} data-testid="profile-logout"
                className="flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-[#c8102e]">
          <LogOut size={14} /> {t("logout")}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 p-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <ProfilePhotoEditor />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg">{user.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user.email}</div>
            {user.role === "admin" && (
              <span className="inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#ffd100] text-black">ADMIN</span>
            )}
            <EmailStatus user={user} t={t} />
          </div>
        </div>
        {user.role === "admin" && (
          <Link to="/admin" data-testid="profile-admin-link"
                className="flex items-center justify-center gap-2 py-2 rounded-xl bg-black text-white text-xs font-semibold hover:bg-[#c8102e] transition-colors">
            <Settings size={13} /> Painel de administrador
          </Link>
        )}
        <Link to="/destaques" data-testid="profile-featured-link"
              className="mt-2 flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl border border-zinc-200 text-xs font-semibold hover:border-black transition-colors">
          <span className="flex items-center gap-2"><Sparkles size={13} /> Os meus destaques</span><span className="text-zinc-400">›</span>
        </Link>
        {destinationsOn && <Link to="/perfil/publicacoes" data-testid="profile-my-posts-link"
              className="mt-2 flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl border border-zinc-200 text-xs font-semibold hover:border-black transition-colors">
          <span className="flex items-center gap-2"><MapPinned size={13} /> As minhas publicações e destinos</span><span className="text-zinc-400">›</span>
        </Link>}
        <Link to="/guardados" data-testid="profile-saved-link"
              className="mt-2 flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl border border-zinc-200 text-xs font-semibold hover:border-black transition-colors">
          <span className="flex items-center gap-2"><Heart size={13} /> Guardados</span><span className="text-zinc-400">›</span>
        </Link>
        <Link to="/notificacoes" data-testid="profile-notifications-link"
              className="mt-2 flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl border border-zinc-200 text-xs font-semibold hover:border-black transition-colors">
          <span className="flex items-center gap-2"><Bell size={13} /> Notificações e preferências</span><span className="text-zinc-400">›</span>
        </Link>
      </div>

      <ProfilePosts userId={user.id} isOwner />

      <div className="bg-white rounded-2xl border border-zinc-200 p-4 mb-4 space-y-3">
        <Field label="Nome"><input data-testid="profile-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="input" /></Field>
        <Field label="Cidade no UK"><input data-testid="profile-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="input" placeholder={user.city_other ? `Outra: ${user.city_other}` : "Ex: London"} /></Field>
        <Field label="Província de origem (Angola)">
          <select data-testid="profile-province" value={form.province} onChange={(e) => set("province", e.target.value)} className="input">
            <option value="">—</option>
            {ANGOLA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Bio curta">
          <textarea data-testid="profile-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={3}
                    className="input resize-none" placeholder="Conta um pouco sobre ti..." />
        </Field>

        <Field label="Interesses">
          <div className="flex flex-wrap gap-1.5">
            {INTERESTS.map(i => (
              <button key={i} type="button" onClick={() => set("interests", toggle(form.interests, i))}
                      className="chip !text-[11px]" data-active={form.interests.includes(i)}>{i}</button>
            ))}
          </div>
        </Field>

        <Field label="À procura de">
          <div className="flex flex-wrap gap-1.5">
            {LOOKING_FOR.map(i => (
              <button key={i} type="button" onClick={() => set("looking_for", toggle(form.looking_for, i))}
                      className="chip !text-[11px]" data-active={form.looking_for.includes(i)}>{i}</button>
            ))}
          </div>
        </Field>

        <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
          {form.profile_visible ? <ShieldCheck size={16} className="text-green-600" /> : <Shield size={16} className="text-zinc-400" />}
          <div className="flex-1 text-xs">
            <div className="font-semibold">Perfil visível para outros membros</div>
            <div className="text-zinc-500">Só mostramos cidade/região, nunca a tua morada.</div>
          </div>
          <button data-testid="profile-visibility-toggle" type="button"
                  onClick={() => set("profile_visible", !form.profile_visible)}
                  className={`w-11 h-6 rounded-full transition-colors ${form.profile_visible ? "bg-[#c8102e]" : "bg-zinc-300"} relative`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.profile_visible ? "translate-x-5" : ""}`} />
          </button>
        </div>

        <button data-testid="profile-save" onClick={save} disabled={busy}
                className="w-full py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 text-white font-semibold transition-colors">
          {busy ? "A guardar..." : "Guardar"}
        </button>
      </div>

      <TutorialsSection />

      <BlockedUsersSection />

      {/* Data rights */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-4 mb-4">
        <h2 className="font-display font-bold text-base mb-1">Os teus dados</h2>
        <p className="text-xs text-zinc-500 leading-relaxed mb-3">
          Podes exportar tudo o que temos sobre ti ou eliminar a tua conta.
        </p>
        <button data-testid="profile-export" onClick={exportData}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-black text-white text-sm font-semibold hover:bg-zinc-800 transition-colors mb-2">
          <Download size={14} /> Exportar os meus dados (JSON)
        </button>
        {user.role !== "admin" && (
          !confirmDelete ? (
            <button data-testid="profile-delete-open" onClick={() => setConfirmDelete(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50 transition-colors">
              <Trash2 size={14} /> Eliminar a minha conta
            </button>
          ) : (
            <div className="border border-red-300 rounded-xl p-3 bg-red-50">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={16} className="text-red-700 shrink-0 mt-0.5" />
                <p className="text-xs text-red-900 leading-relaxed">
                  Vais eliminar permanentemente a tua conta e ocultar todas as tuas publicações. Alguns registos legais (consentimentos, denúncias) podem ser retidos conforme exigido por lei. <strong>Esta ação não pode ser desfeita.</strong>
                </p>
              </div>
              <input
                data-testid="profile-delete-confirm"
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                placeholder='Escreve "ELIMINAR"'
                className="w-full px-3 py-2 rounded-lg border border-red-300 bg-white text-sm outline-none focus:border-red-600 mb-2"
              />
              <div className="flex gap-2">
                <button onClick={() => { setConfirmDelete(false); setDeleteText(""); }}
                        className="flex-1 py-2 rounded-lg border border-zinc-300 bg-white text-xs font-semibold hover:bg-zinc-50">
                  Cancelar
                </button>
                <button data-testid="profile-delete-confirm-btn" onClick={deleteAccount} disabled={deleting}
                        className="flex-1 py-2 rounded-lg bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white text-xs font-semibold">
                  {deleting ? "A eliminar..." : "Eliminar definitivamente"}
                </button>
              </div>
            </div>
          )
        )}
      </div>

      <div className="mb-6"><LegalFooter /></div>

      <style>{`.input{width:100%;padding:.6rem .75rem;border-radius:.6rem;border:1px solid #e4e4e7;background:#fafafa;font-size:.85rem;outline:none;transition:border-color .15s}.input:focus{border-color:#0d0d0d}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function EmailStatus({ user, t }) {
  const { resend, cooldown, busy } = useResendVerification();
  if (user.email_verified !== false) {
    return <div data-testid="profile-email-status" data-status="verified" className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-green-700"><MailCheck size={12} /> {t("verify_status_ok")}</div>;
  }
  return (
    <div data-testid="profile-email-status" data-status="pending" className="mt-1 flex items-center gap-2 text-[11px] font-semibold text-[#a15c00]">
      <span className="inline-flex items-center gap-1"><MailWarning size={12} /> {t("verify_status_pending")}</span>
      <button onClick={resend} disabled={busy || cooldown > 0} data-testid="profile-verify-resend" className="underline disabled:opacity-50">
        {cooldown > 0 ? `${cooldown}s` : t("verify_resend")}
      </button>
    </div>
  );
}
